// Rechte Seite des Kopfs jeder Lead-Liste und Smart View, Reihenfolge wie in Close:
// Massen-Mail · Telefon ▾ (Power Dialer) · Blitz ▾ (Workflows, `workflows/WorkflowBlitz.tsx`) · Speichern bzw. Speichern unter · „…“ · Trenner · „+“ (Neuer Lead).
// Runde 5 (Agent 5A): „Speichern“ (Smart View, nur nach einer Filteränderung), „Speichern unter“ (Leads-Liste und im Menü), „Smart View
// bearbeiten“ (Name, Emoji, Freigabe, Löschen) und „Filter kopieren“. Der Filter-Entwurf kommt von der Seite (`FilterZeile` setzt ihn).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fehlerText } from "../../api";
import { smartViewsSchreibenApi, type FilterEntwurf } from "../../api/filter";
import { useAuth } from "../../auth/AuthContext";
import { smartViewsLaden } from "../../daten/smartviews";
import { massenApi, type Ziel } from "../../api/leadsNeu";
import type { SmartView, SmartViewKurz } from "../../api/types";
import { kopieren } from "../../werkzeuge/tasten";
import { Icon } from "../Icon";
import { MenueKnopf, type MenuePunkt } from "../Menue";
import { useMeldung } from "../Meldungen";
import { WorkflowBlitz } from "../../workflows";
import { dateiSpeichern } from "./csv";
import { FILTER_TEXTE } from "./filterTexte";
import { SmartViewBearbeitenDialog, SpeichernUnterDialog } from "./FilterSpeichern";
import { useDialerStart } from "./dialer";
import { DialerVerwaltenDialog } from "./DialerVerwaltenDialog";
import { leadsImportierenOeffnen, neuerLeadOeffnen } from "./dialogBus";
import { MassenbearbeitungDialog } from "./MassenbearbeitungDialog";
import { MassenMailDialog } from "./MassenMailDialog";
import "./smartview.css";

const NICHT_GESPEICHERT = "Als Smart View speichern, um mit dem Power Dialer anzurufen";
const T = FILTER_TEXTE.speichern;
/** Spalten einer neuen Smart View von der Leads-Liste (wie die Spalten der Liste). */
const LEADS_SPALTEN = ["display_name", "status_id", "primary_contact_name", "date_created", "date_updated"].map((field_id) => ({ field_id, type_id: "lead" }));

function alsKurz(v: SmartView): SmartViewKurz {
  return { id: v.id, name: v.name, type: v.type, is_shared: v.is_shared, owner_id: v.owner_id, pinned: false, pin_position: null };
}

interface Props {
  titel: string;
  /** gespeicherte Smart View; ohne = ungespeicherte Suche bzw. Leads-Liste */
  viewId?: string;
  /** alle Treffer der Liste (für Massen-Mail, Massenbearbeitung, Export) */
  ziel: Ziel;
  trefferZahl: number | null;
  markierte: string[];
  onAlleMarkieren?: () => void;
  onMarkierungAufheben?: () => void;
  angeheftet?: boolean;
  onAnheften?: () => void;
  /** Abfrage der Liste für „Filter kopieren (JSON)“ */
  filterJson?: unknown;
  /** Link zum Bearbeiten der Smart View in Close (nur Close-eigene Views) */
  closeUrl?: string | null;
  onNeuLaden?: () => void;
  /** Runde 5 (Agent 5A, "Speichern", "Speichern unter", "Copy Filters"): gespeicherte Definition der Smart View, Filter-Entwurf
   *  der Seite und Rückmeldung nach dem Speichern. Die Seiten übergeben sie schon; bis 5A sie nutzt, ändert sich nichts. */
  view?: SmartView | null;
  entwurf?: FilterEntwurf | null;
  onGespeichert?: (view: SmartViewKurz) => void;
}

function dateiName(titel: string): string {
  const roh = titel.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  return `${roh || "leads"}-${new Date().toISOString().slice(0, 10)}.csv`;
}

export function ListenKopfRechts({ titel, viewId, ziel, trefferZahl, markierte, onAlleMarkieren, onMarkierungAufheben, angeheftet, onAnheften, filterJson, closeUrl, onNeuLaden, view, entwurf, onGespeichert }: Props) {
  const meldung = useMeldung();
  const navigate = useNavigate();
  const { nutzer } = useAuth();
  const dialer = useDialerStart();
  const [unterOffen, setUnterOffen] = useState(false);
  const [svBearbeitenOffen, setSvBearbeitenOffen] = useState(false);
  const [loeschenGleich, setLoeschenGleich] = useState(false);
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);
  const [verwaltenOffen, setVerwaltenOffen] = useState(false);
  const [exportiert, setExportiert] = useState(false);

  const gespeichert = !!viewId;
  // Wurde die Smart View im CRM aus einer Close-Smart-View ersetzt, kann die Adresse noch die alte ID tragen; Power Dialer und
  // Workflows brauchen die ID der gültigen Fassung (`view.id`, siehe `smartviews.hole`).
  const gueltigeId = view?.id ?? viewId;
  const darfTeilen = nutzer?.permissions.includes("manage_team_smart_views") ?? false;
  const kannBearbeiten = !view?.kann || view.kann.bearbeiten;

  /** „Speichern“: der Filter-Entwurf wird zur gespeicherten Abfrage. Bei einer Close-Smart-View entsteht dabei die CRM-Kopie. */
  const speichern = async () => {
    if (!entwurf || !view || speichernLaeuft) return;
    setSpeichernLaeuft(true);
    try {
      const r = await smartViewsSchreibenApi.aendern(view.id, { s_query: entwurf.s_query });
      void smartViewsLaden();
      meldung.zeige("ok", view.source === "close" || r.smart_view.id !== view.id ? T.gespeichertKopie : T.gespeichert);
      onGespeichert?.(alsKurz(r.smart_view));
    } catch (e) {
      meldung.fehler(e, "Speichern");
    }
    setSpeichernLaeuft(false);
  };

  /** „Speichern unter“: neue Smart View aus dem Entwurf (oder, ohne Entwurf, aus der gespeicherten Abfrage). */
  const speichernUnter = async (name: string, geteilt: boolean) => {
    const abfrage = entwurf?.s_query ?? view?.s_query ?? undefined;
    const r = await smartViewsSchreibenApi.neu({
      name,
      type: "lead",
      ...(abfrage ? { s_query: abfrage } : {}),
      selected_fields: view?.selected_fields ?? (LEADS_SPALTEN as never),
      is_shared: geteilt,
      kopie_von: view?.id ?? null,
    });
    void smartViewsLaden();
    meldung.zeige("ok", T.angelegt);
    onGespeichert?.(alsKurz(r.smart_view));
  };
  const aktivesZiel: Ziel = markierte.length ? { lead_ids: markierte } : ziel;
  const zielText = markierte.length ? `${markierte.length.toLocaleString("de-DE")} markierte${markierte.length === 1 ? "r Lead" : " Leads"}` : trefferZahl !== null ? `alle ${trefferZahl.toLocaleString("de-DE")} Treffer` : "alle Treffer";

  const dialerFehler = (e: unknown) => meldung.zeige("fehler", fehlerText(e));
  const telefonStarten = () => {
    if (!gueltigeId) return;
    const laeuft = dialer.laeuftFuer === gueltigeId;
    const aufgabe = laeuft && dialer.pausiert ? dialer.weiter() : dialer.starten(gueltigeId, "weiter");
    aufgabe.catch(dialerFehler);
  };

  const telefonGrund = !gespeichert ? NICHT_GESPEICHERT : dialer.grund;
  const telefonPunkte: MenuePunkt[] = [
    ...(!gespeichert ? [{ label: NICHT_GESPEICHERT, ueberschrift: true } as MenuePunkt] : []),
    {
      label: "Weiter anrufen",
      icon: "telefon",
      hinweis: "Alle Leads, die in dieser Sitzung noch nicht angerufen oder in der letzten Stunde nicht erreicht wurden",
      deaktiviert: !gespeichert || !dialer.bereit,
      onClick: telefonStarten,
    },
    {
      label: "Von vorn beginnen",
      icon: "neu-laden",
      hinweis: "Alle Leads der Ansicht neu",
      deaktiviert: !gespeichert || !dialer.bereit,
      onClick: () => gueltigeId && dialer.starten(gueltigeId, "von_vorn").catch(dialerFehler),
    },
    { label: "Verwalten", icon: "einstellungen", hinweis: "Anrufe heute, wer gerade anruft, Klingeldauer", deaktiviert: !gespeichert, onClick: () => setVerwaltenOffen(true) },
  ];

  const exportieren = async () => {
    if (exportiert) return;
    setExportiert(true);
    try {
      const r = await massenApi.exportCsv(aktivesZiel);
      dateiSpeichern(dateiName(titel), r.csv);
      meldung.ok(`${r.anzahl.toLocaleString("de-DE")} ${r.anzahl === 1 ? "Lead" : "Leads"} exportiert${r.gekappt ? " (höchstens 2.000 je Export)" : ""}.`);
    } catch (e) {
      meldung.fehler(e, "Export");
    }
    setExportiert(false);
  };

  const filterKopieren = () => {
    const inhalt = filterJson ?? null;
    if (inhalt === null) {
      meldung.zeige("info", "Diese Liste hat keinen gespeicherten Filter.");
      return;
    }
    void kopieren(JSON.stringify(inhalt, null, 2)).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? "Filter als JSON kopiert." : "Kopieren hat nicht geklappt."));
  };

  const mehrPunkte: MenuePunkt[] = [
    {
      label: "Link kopieren",
      icon: "link",
      onClick: () => void kopieren(window.location.href).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? "Link kopiert." : "Kopieren hat nicht geklappt.")),
    },
    viewId && view
      ? { label: T.bearbeiten, icon: "stift", hinweis: kannBearbeiten ? T.bearbeitenKlein : T.keinRecht, onClick: () => (setLoeschenGleich(false), setSvBearbeitenOffen(true)) }
      : { label: T.bearbeiten, icon: "stift", deaktiviert: true, hinweis: gespeichert ? "Die Smart View wird geladen" : "Nur gespeicherte Smart Views lassen sich bearbeiten" },
    { label: T.speichernUnter, icon: "kopieren", hinweis: T.speichernUnterTitel, onClick: () => setUnterOffen(true) },
    ...(viewId && onAnheften
      ? [{ label: angeheftet ? "Aus Seitenleiste entfernen" : "An Seitenleiste anheften", icon: "stecknadel", onClick: onAnheften } as MenuePunkt]
      : []),
    ...(onNeuLaden ? [{ label: "Neu laden", icon: "neu-laden", onClick: onNeuLaden } as MenuePunkt] : []),
    { label: "Export CSV", icon: "kopieren", hinweis: `${zielText} als CSV speichern`, onClick: () => void exportieren(), trenner: true },
    { label: "Filter kopieren (JSON)", icon: "filter", deaktiviert: filterJson === undefined, onClick: filterKopieren },
    ...(closeUrl ? [{ label: "In Close ansehen", icon: "extern", hinweis: "Die Fassung in Close; Änderungen im CRM gelten dort nicht", onClick: () => window.open(closeUrl, "_blank", "noopener") } as MenuePunkt] : []),

    { label: "Leads importieren", icon: "plus", onClick: leadsImportierenOeffnen, trenner: true },
    ...(onAlleMarkieren ? [{ label: "Alle geladenen Leads markieren", icon: "haken", onClick: onAlleMarkieren, trenner: true } as MenuePunkt] : []),
    ...(markierte.length && onMarkierungAufheben ? [{ label: `Markierung aufheben (${markierte.length})`, icon: "schliessen", onClick: onMarkierungAufheben } as MenuePunkt] : []),
    { label: `Massenbearbeitung (${markierte.length ? `${markierte.length} markiert` : "alle Treffer"})`, icon: "stift", onClick: () => setBearbeitenOffen(true), trenner: true },
    ...(viewId && view && view.kann?.loeschen !== false ? [{ label: T.loeschen, icon: "papierkorb", gefahr: true, trenner: true, onClick: () => (setLoeschenGleich(true), setSvBearbeitenOffen(true)) } as MenuePunkt] : []),
  ];

  return (
    <div className="slk">
      <button type="button" className="symbolknopf symbolknopf--rand" title={`Massen-Mail an ${zielText}`} aria-label="Massen-Mail" onClick={() => setMailOffen(true)}>
        <Icon name="mail" groesse={16} />
      </button>
      <div className="slk__gruppe">
        <button
          type="button"
          className="symbolknopf symbolknopf--rand"
          title={telefonGrund ?? (dialer.laeuftFuer === gueltigeId && dialer.pausiert ? "Power Dialer fortsetzen" : "Leads dieser Smart View mit dem Power Dialer anrufen")}
          aria-label="Power Dialer starten"
          disabled={!gespeichert || !dialer.bereit}
          onClick={telefonStarten}
        >
          <Icon name="telefon" groesse={16} />
        </button>
        <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand slk__pfeil" titel="Power-Dialer-Menü" punkte={telefonPunkte} ausrichtung="end" breite={300}>
          <Icon name="chevron-unten" groesse={12} />
        </MenueKnopf>
      </div>
      <div className="slk__gruppe">
        <WorkflowBlitz kontext={{ art: "smartview", viewId: gueltigeId, titel, ziel, markierte, trefferZahl }} />
      </div>
      {gespeichert ? (
        <button
          type="button"
          className={`knopf slk__speichern${entwurf && kannBearbeiten ? " knopf--primaer" : ""}`}
          disabled={!entwurf || !view || !kannBearbeiten || speichernLaeuft}
          title={!entwurf ? T.nichtsGeaendert : !kannBearbeiten ? T.keinRecht : T.speichernTitel}
          onClick={() => void speichern()}
        >
          {speichernLaeuft ? "Speichert …" : T.speichern}
        </button>
      ) : (
        <button type="button" className={`knopf slk__speichern${entwurf ? " knopf--primaer" : ""}`} disabled={!entwurf} title={entwurf ? T.speichernUnterTitel : T.nichtsGeaendert} onClick={() => setUnterOffen(true)}>
          {T.speichernUnterKnopf}
        </button>
      )}
      <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand" titel="Mehr" punkte={mehrPunkte} ausrichtung="end" breite={300}>
        <Icon name="mehr" groesse={16} />
      </MenueKnopf>
      <span className="slk__trenner" role="separator" />
      <button type="button" className="symbolknopf symbolknopf--rand" title="Neuer Lead (⌘⇧L)" aria-label="Neuer Lead" onClick={() => neuerLeadOeffnen()}>
        <Icon name="plus" groesse={16} />
      </button>

      <MassenMailDialog offen={mailOffen} onSchliessen={() => setMailOffen(false)} ziel={aktivesZiel} titel={titel} />
      <MassenbearbeitungDialog
        offen={bearbeitenOffen}
        onSchliessen={() => setBearbeitenOffen(false)}
        ziel={aktivesZiel}
        titel={titel}
        beschreibung={zielText}
        onFertig={onNeuLaden}
      />
      {gueltigeId && viewId ? <DialerVerwaltenDialog offen={verwaltenOffen} onSchliessen={() => setVerwaltenOffen(false)} viewId={gueltigeId} titel={titel} /> : null}
      <SpeichernUnterDialog
        offen={unterOffen}
        onSchliessen={() => setUnterOffen(false)}
        vorschlag={view ? `${view.name} (Kopie)` : "Neue Smart View"}
        darfTeilen={darfTeilen}
        kopieVon={view}
        onSpeichern={speichernUnter}
      />
      <SmartViewBearbeitenDialog
        offen={svBearbeitenOffen}
        onSchliessen={() => setSvBearbeitenOffen(false)}
        view={view ?? null}
        darfTeilen={darfTeilen}
        loeschenGleich={loeschenGleich}
        onGespeichert={(v) => {
          void smartViewsLaden();
          meldung.zeige("ok", v.source === "crm" && view?.source === "close" ? T.gespeichertKopie : T.gespeichert);
          onGespeichert?.(alsKurz(v));
        }}
        onGeloescht={(v) => {
          void smartViewsLaden();
          meldung.zeige("ok", v.source === "close" ? T.geloeschtClose : T.geloescht);
          navigate("/smart-views/alle");
        }}
      />
    </div>
  );
}
